import apiClient from './client'
import type {
  CreateListRequest,
  ItemQuery,
  ListCounts,
  ListItemsPage,
  SavedList,
  UpdateListRequest,
} from '@/types/models'
import {
  adaptItemQuery,
  adaptItemResponse,
  adaptListCountsResponse,
  adaptListMembershipsResponse,
  adaptSavedListResponse,
} from '@/lib/utils/api-adapter'
import { normalizeCriteria } from '@/lib/utils/list-criteria'

function adaptListRequest(data: CreateListRequest | UpdateListRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (data.name !== undefined) body.name = data.name
  if (data.description !== undefined) body.description = data.description
  if (data.kind !== undefined) body.kind = data.kind
  // Empty string clears icon / color, so send them through untouched.
  if (data.icon !== undefined) body.icon = data.icon
  if (data.color !== undefined) body.color = data.color
  if (data.position !== undefined) body.position = data.position
  if (data.criteria !== undefined) body.criteria = normalizeCriteria(data.criteria)
  return body
}

/**
 * Saved-list API endpoints (rivulet >= v0.14.0).
 *
 * Membership is evaluated server-side only — never filter a list locally.
 */
export const listsApi = {
  /**
   * The user's lists ordered by position, then creation time.
   */
  async list(): Promise<SavedList[]> {
    const response = await apiClient.get<any>('/api/v1/lists')
    return (response.lists ?? []).map(adaptSavedListResponse)
  },

  /**
   * One list plus, for query lists, the feed set its criteria resolve to.
   */
  async get(id: string): Promise<SavedList> {
    const response = await apiClient.get<any>(`/api/v1/lists/${id}`)
    return adaptSavedListResponse(response)
  },

  async create(data: CreateListRequest): Promise<SavedList> {
    const response = await apiClient.post<any>('/api/v1/lists', adaptListRequest(data))
    return adaptSavedListResponse(response)
  },

  async update(id: string, data: UpdateListRequest): Promise<SavedList> {
    const response = await apiClient.patch<any>(`/api/v1/lists/${id}`, adaptListRequest(data))
    return adaptSavedListResponse(response)
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete<void>(`/api/v1/lists/${id}`)
  },

  /**
   * The list's members. The stored criteria apply first; `query` only
   * narrows them further.
   */
  async items(id: string, query?: ItemQuery): Promise<ListItemsPage> {
    const response = await apiClient.get<any>(`/api/v1/lists/${id}/items`, {
      params: adaptItemQuery(query),
    })
    const items = (response.items ?? []).map((item: any) => ({
      ...adaptItemResponse(item),
      feed: item.feed,
    }))
    return {
      items,
      memberships: adaptListMembershipsResponse(response.memberships),
      total: response.total ?? items.length,
      limit: response.limit ?? items.length,
      offset: response.offset ?? 0,
      hasMore: response.has_more ?? false,
    }
  },

  /**
   * Unread counts for EVERY list in one call, plus the `all` block behind
   * the built-in Unread / Starred rows. One call per refresh — never one
   * per list.
   */
  async counts(): Promise<ListCounts> {
    const response = await apiClient.get<any>('/api/v1/lists/counts')
    return adaptListCountsResponse(response)
  },

  /**
   * Mark the list's unread members read.
   *
   * `before` must be the timestamp of the load being acknowledged (not the
   * newest published_at seen): the server bounds the update on `created_at`,
   * so "mark everything I loaded" stays race-free against ingest.
   */
  async markRead(id: string, before: string): Promise<{ marked: number }> {
    const response = await apiClient.post<any>(`/api/v1/lists/${id}/mark-read`, { before })
    return { marked: response?.marked ?? 0 }
  },
}

export default listsApi
